import React from "react";
import { Loader2, XCircle } from "lucide-react";
import { Alert as BaseAlert } from "./Feedback";
import { Box, Container, Grid, Paper, sxToStyle, Typography } from "./LayoutPrimitives";
import { cn } from "./utils";

const toneBySeverity = {
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
};

const getNestedStyle = (sx, token) => {
  if (!sx || typeof sx !== "object") return {};
  const match = Object.entries(sx).find(([key]) => String(key).includes(token));
  return match?.[1] && typeof match[1] === "object" ? match[1] : {};
};

const stripNestedStyle = (style = {}) =>
  Object.fromEntries(Object.entries(style || {}).filter(([key]) => !String(key).startsWith("&") && !String(key).startsWith("@")));

const defaultTheme = {
  breakpoints: {
    down: (key) => {
      const width = { xs: 480, sm: 640, md: 768, lg: 1024, xl: 1280 }[key] || 640;
      return `(max-width: ${width}px)`;
    },
  },
  palette: {
    mode: "light",
    common: { black: "#000000", white: "#ffffff" },
    primary: { light: "#8F8ABD", main: "#746EAC", dark: "#5A5590", contrastText: "#ffffff" },
    secondary: { light: "#5AA8A9", main: "#3B9394", dark: "#2A7576", contrastText: "#ffffff" },
    success: { light: "#81C784", main: "#4CAF50", dark: "#388E3C", contrastText: "#ffffff" },
    error: { light: "#E57373", main: "#F44336", dark: "#D32F2F", contrastText: "#ffffff" },
    warning: { light: "#FFB74D", main: "#FF9800", dark: "#F57C00", contrastText: "#ffffff" },
    info: { light: "#64B5F6", main: "#2196F3", dark: "#1976D2", contrastText: "#ffffff" },
    text: { primary: "#1e293b", secondary: "#64748b", disabled: "#94a3b8" },
    background: { default: "#f8fafc", paper: "#ffffff" },
    divider: "#e2e8f0",
    action: {
      active: "#1e293b",
      hover: "rgba(0, 0, 0, 0.04)",
      selected: "rgba(0, 0, 0, 0.08)",
      disabled: "rgba(0, 0, 0, 0.26)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
      A100: "#f5f5f5",
      A200: "#eeeeee",
      A400: "#bdbdbd",
      A700: "#616161",
    },
  },
  spacing: (value) => `${Number(value || 0) * 8}px`,
};

export const useTheme = () => defaultTheme;

export const useMediaQuery = (query) => {
  const getMatches = React.useCallback(() => {
    if (typeof window === "undefined" || !window.matchMedia || !query) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  const [matches, setMatches] = React.useState(getMatches);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia || !query) return undefined;
    const mediaQueryList = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQueryList.matches);
    handleChange();
    mediaQueryList.addEventListener?.("change", handleChange);
    mediaQueryList.addListener?.(handleChange);
    return () => {
      mediaQueryList.removeEventListener?.("change", handleChange);
      mediaQueryList.removeListener?.(handleChange);
    };
  }, [query]);

  return matches;
};

export const styled = (Component) => (styleFactory) => {
  const StyledComponent = ({ style, sx, ...props }) => {
    // Pass component props (plus theme) into the style factory, mirroring MUI's styled().
    const baseStyle = typeof styleFactory === "function" ? styleFactory({ theme: defaultTheme, ...props }) : styleFactory;

    // Don't forward style-only object props (e.g. `readingsettings`) to the DOM element.
    const forwardedProps = {};
    Object.entries(props).forEach(([key, value]) => {
      const isPlainObject =
        value && typeof value === "object" && !Array.isArray(value) && !React.isValidElement(value);
      if (key !== "children" && isPlainObject) return;
      forwardedProps[key] = value;
    });

    return <Component {...forwardedProps} sx={sx} style={{ ...stripNestedStyle(baseStyle), ...(style || {}) }} />;
  };
  StyledComponent.displayName = `Styled${Component.displayName || Component.name || "Component"}`;
  return StyledComponent;
};

export { Box, Container, Grid, Paper, Typography };

export const Drawer = ({ anchor = "right", children, onClose, open, PaperProps = {}, sx }) => {
  if (!open) return null;
  const sideStyle = anchor === "left" ? { left: 0 } : { right: 0 };
  const paperSx = {
    ...getNestedStyle(sx, "drawer-paper"),
    ...(PaperProps.sx || {}),
  };

  return (
    <div className="ui-drawer-root fixed inset-0 z-50">
      <button
        aria-label="Close drawer backdrop"
        className="ui-backdrop absolute inset-0 bg-slate-950/35"
        onClick={onClose}
        type="button"
      />
      <aside
        className={cn("ui-drawer ui-drawer-paper absolute top-0 h-full overflow-auto bg-white shadow-2xl", PaperProps.className)}
        style={{
          width: 420,
          maxWidth: "100vw",
          ...sideStyle,
          ...sxToStyle(paperSx),
          ...(PaperProps.style || {}),
        }}
      >
        {children}
      </aside>
    </div>
  );
};

export const IconButton = ({ children, className, color, disabled, edge, onClick, size = "medium", sx, style, ...props }) => {
  const dimension = size === "small" ? 32 : 40;
  return (
    <button
      className={cn("ui-icon-button inline-flex items-center justify-center rounded-full border-0 bg-transparent transition disabled:cursor-not-allowed disabled:opacity-50", className)}
      data-edge={edge}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: dimension,
        height: dimension,
        color: color === "primary" ? "#746EAC" : undefined,
        cursor: disabled ? "not-allowed" : "pointer",
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
};

export const Button = ({
  children,
  className,
  color,
  disableElevation,
  disableRipple,
  disabled,
  endIcon,
  fullWidth,
  onClick,
  size = "medium",
  startIcon,
  sx,
  style,
  type = "button",
  variant = "text",
  ...props
}) => {
  const sizeStyle = size === "small"
    ? { minHeight: 32, padding: "6px 10px", fontSize: "0.75rem" }
    : { minHeight: 40, padding: "8px 14px", fontSize: "0.875rem" };
  const colorStyle = color === "primary" && variant === "text" ? { color: "#746EAC" } : {};
  const variantStyle = {
    contained: { backgroundColor: "#746EAC", borderColor: "#746EAC", color: "#ffffff" },
    outlined: { backgroundColor: "#ffffff", borderColor: "#d1d5db", color: "#334155" },
    text: { backgroundColor: "transparent", borderColor: "transparent", color: "#746EAC" },
  }[variant] || {};

  return (
    <button
      className={cn("ui-button inline-flex items-center justify-center gap-2 rounded-md border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60", `ui-button-${variant === "contained" ? "primary" : variant}`, className)}
      data-disable-ripple={disableRipple ? "true" : undefined}
      data-ui-button={variant === "contained" ? "primary" : variant}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: fullWidth ? "100%" : undefined,
        cursor: disabled ? "not-allowed" : "pointer",
        ...sizeStyle,
        ...variantStyle,
        ...colorStyle,
        boxShadow: disableElevation ? "none" : undefined,
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      type={type}
      {...props}
    >
      {startIcon}
      {children}
      {endIcon}
    </button>
  );
};

export const TextField = ({
  children,
  className,
  disabled,
  error,
  fullWidth,
  helperText,
  InputLabelProps,
  inputProps,
  label,
  maxRows,
  multiline,
  onChange,
  onKeyDown,
  onKeyPress,
  placeholder,
  rows,
  select,
  sx,
  value,
  variant,
  ...props
}) => {
  const rootSx = getNestedStyle(sx, "outlined-input-root");
  const fieldsetSx = getNestedStyle(rootSx, "fieldset");
  const InputComponent = select ? "select" : multiline ? "textarea" : "input";
  const fieldProps = {
    className: cn("ui-input ui-outlined-input-root rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100", className),
    "data-variant": variant,
    disabled,
    onChange,
    onKeyDown,
    onKeyPress,
    placeholder,
    rows: multiline ? rows || maxRows || 2 : undefined,
    style: {
      width: fullWidth ? "100%" : undefined,
      minHeight: multiline ? 44 : 40,
      resize: multiline ? "vertical" : undefined,
      ...sxToStyle(sx),
      ...sxToStyle(rootSx),
      borderColor: fieldsetSx.borderColor,
    },
    value,
    "aria-invalid": error ? "true" : undefined,
    ...(inputProps || {}),
    ...props,
  };

  const field = !select && !multiline
    ? <input {...fieldProps} />
    : <InputComponent {...fieldProps}>{children}</InputComponent>;

  if (!label && !helperText) return field;

  return (
    <label className="ui-text-field block w-full">
      {label && (
        <span className="mb-1 block text-sm font-medium text-slate-700" data-shrink={InputLabelProps?.shrink ? "true" : undefined}>
          {label}
        </span>
      )}
      {field}
      {helperText && (
        <span className={cn("mt-1 block text-xs", error ? "text-red-600" : "text-slate-500")}>
          {helperText}
        </span>
      )}
    </label>
  );
};

export const FormControl = ({ children, className, fullWidth, size, sx, style, ...props }) => (
  <div
    className={cn("ui-form-control flex flex-col gap-1", size === "small" ? "text-sm" : "", className)}
    style={{ width: fullWidth ? "100%" : undefined, ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {children}
  </div>
);

export const InputLabel = ({ children, className, sx, style, ...props }) => (
  <label className={cn("ui-input-label text-xs font-semibold text-slate-600", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </label>
);

export const Select = ({ children, className, disabled, label, labelId, onChange, size, sx, style, value, ...props }) => (
  <select
    aria-label={label}
    aria-labelledby={labelId}
    className={cn("ui-select ui-outlined-input-root rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100", size === "small" ? "py-1.5" : "", className)}
    disabled={disabled}
    onChange={onChange}
    style={{ minHeight: size === "small" ? 34 : 40, ...sxToStyle(sx), ...(style || {}) }}
    value={value}
    {...props}
  >
    {children}
  </select>
);

export const Breadcrumbs = ({ children, className, separator = "/", sx, style, ...props }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <nav aria-label={props["aria-label"] || "breadcrumb"} className={cn("ui-breadcrumbs flex flex-wrap items-center gap-2 text-sm text-slate-600", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span className="text-slate-400">{separator}</span>}
          <span>{item}</span>
        </React.Fragment>
      ))}
    </nav>
  );
};

export const Accordion = ({ children, className, expanded = false, onChange, sx, style, ...props }) => {
  const handleToggle = (event) => onChange?.(event, !expanded);
  return (
    <div className={cn("ui-accordion overflow-hidden rounded-md border border-slate-200 bg-white", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        if (child.type?.displayName === "AccordionSummary") {
          return React.cloneElement(child, { expanded, onToggle: handleToggle });
        }
        if (child.type?.displayName === "AccordionDetails") {
          return React.cloneElement(child, { hidden: !expanded });
        }
        return child;
      })}
    </div>
  );
};

export const AccordionSummary = ({ children, className, expandIcon, expanded, onToggle, sx, style, ...props }) => (
  <div
    className={cn("ui-accordion-summary flex cursor-pointer items-center gap-3 px-4 py-3", className)}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle?.(event);
      }
    }}
    role="button"
    style={{ ...sxToStyle(sx), ...(style || {}) }}
    tabIndex={0}
    {...props}
  >
    <div className="min-w-0 flex-1">{children}</div>
    {expandIcon && (
      <span className={cn("ui-accordion-expand-icon inline-flex transition-transform", expanded ? "rotate-180" : "")}>
        {expandIcon}
      </span>
    )}
  </div>
);
AccordionSummary.displayName = "AccordionSummary";

export const AccordionDetails = ({ children, className, hidden, sx, style, ...props }) => {
  if (hidden) return null;
  return (
    <div className={cn("ui-accordion-details px-4 py-3", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {children}
    </div>
  );
};
AccordionDetails.displayName = "AccordionDetails";

export const AppBar = ({ children, className, elevation, position = "static", sx, style, ...props }) => (
  <header
    className={cn("ui-app-bar w-full", elevation ? "shadow" : "", className)}
    style={{ position, ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {children}
  </header>
);

export const Toolbar = ({ children, className, sx, style, ...props }) => (
  <div className={cn("ui-toolbar flex min-h-[64px] items-center", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </div>
);

export const Fab = ({ children, className, color, disabled, onClick, size = "large", sx, style, ...props }) => {
  const dimension = size === "small" ? 40 : size === "medium" ? 48 : 56;
  return (
    <button
      className={cn("ui-fab inline-flex items-center justify-center rounded-full border-0 shadow-lg transition disabled:cursor-not-allowed disabled:opacity-60", className)}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: dimension,
        height: dimension,
        backgroundColor: color === "primary" ? "#746EAC" : "#746EAC",
        color: "#ffffff",
        cursor: disabled ? "not-allowed" : "pointer",
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
};

export const Skeleton = ({ className, height, sx, style, variant = "text", width, ...props }) => {
  const shapeClass = {
    circular: "rounded-full",
    rectangular: "rounded-md",
    rounded: "rounded-md",
    text: "rounded",
  }[variant] || "rounded";

  return (
    <span
      className={cn("ui-skeleton block animate-pulse bg-slate-200", shapeClass, className)}
      style={{
        width: width || "100%",
        height: height || (variant === "text" ? 16 : 120),
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      {...props}
    />
  );
};

export const Chip = ({ className, clickable, color, icon, label, onClick, size = "medium", sx, style, variant, ...props }) => {
  const Component = onClick ? "button" : "span";
  const isClickable = Boolean(onClick || clickable);
  const isPrimary = color === "primary";
  const isOutlined = variant === "outlined";
  return (
    <Component
      className={cn("ui-chip inline-flex items-center gap-1 rounded-full border border-transparent font-semibold transition", isClickable ? "cursor-pointer" : "cursor-default", className)}
      onClick={onClick}
      style={{
        backgroundColor: isPrimary ? "#746EAC" : isOutlined ? "#ffffff" : "#f1f5f9",
        borderColor: isOutlined ? (isPrimary ? "#746EAC" : "#cbd5e1") : "transparent",
        color: isPrimary ? "#ffffff" : "#334155",
        minHeight: size === "small" ? 22 : 28,
        padding: size === "small" ? "0 8px" : "0 10px",
        fontSize: size === "small" ? "0.75rem" : "0.875rem",
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      type={onClick ? "button" : undefined}
      {...props}
    >
      {icon && <span className="ui-chip-icon inline-flex">{icon}</span>}
      <span className="ui-chip-label">{label}</span>
    </Component>
  );
};

export const Badge = ({ badgeContent, children, className, color, invisible, max = 99, sx, style, ...props }) => {
  const content = typeof badgeContent === "number" && badgeContent > max ? `${max}+` : badgeContent;
  return (
    <span className={cn("ui-badge relative inline-flex", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {children}
      {!invisible && content !== undefined && content !== null && (
        <span
          className="ui-badge-content absolute -right-1 -top-1 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
          style={{ backgroundColor: color === "primary" ? "#746EAC" : "#ef4444" }}
        >
          {content}
        </span>
      )}
    </span>
  );
};

export const Tooltip = ({ children, title }) => (
  <span className="ui-tooltip inline-flex" title={title}>
    {children}
  </span>
);

export const Fade = ({ children, in: isOpen, style }) => {
  if (!isOpen) return null;
  return React.isValidElement(children)
    ? React.cloneElement(children, { style: { ...(children.props.style || {}), ...(style || {}) } })
    : children;
};

export const Zoom = Fade;

export const Card = ({ children, className, elevation, sx, style, ...props }) => (
  <Paper className={cn("ui-card", className)} elevation={elevation} sx={sx} style={style} {...props}>
    {children}
  </Paper>
);

export const CardContent = ({ children, className, sx, style, ...props }) => (
  <div className={cn("ui-card-content", className)} style={{ padding: 16, ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </div>
);

export const CardMedia = ({ alt, className, component = "img", image, src, sx, style, ...props }) => {
  const Component = component || "img";
  return (
    <Component
      alt={alt}
      className={cn("ui-card-media block w-full object-cover", className)}
      src={src || image}
      style={{ ...sxToStyle(sx), ...(style || {}) }}
      {...props}
    />
  );
};

export const List = ({ children, className, sx, style, ...props }) => (
  <ul className={cn("ui-list", className)} style={{ listStyle: "none", margin: 0, padding: 0, ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </ul>
);

export const ListItem = ({ children, className, sx, style, ...props }) => (
  <li className={cn("ui-list-item", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </li>
);

export const ListItemIcon = ({ children, className, sx, style, ...props }) => (
  <span className={cn("ui-list-item-icon inline-flex shrink-0 items-center", className)} style={{ minWidth: 32, ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </span>
);

export const Avatar = ({ children, className, sx, style, ...props }) => (
  <span
    className={cn("ui-avatar inline-flex items-center justify-center rounded-full", className)}
    style={{ width: 40, height: 40, overflow: "hidden", ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {children}
  </span>
);

export const LinearProgress = ({ className, sx, value = 0 }) => (
  <div className={cn("ui-linear-progress overflow-hidden rounded-full bg-slate-200", className)} style={{ height: 6, ...sxToStyle(sx) }}>
    <div
      className="ui-linear-progress-bar"
      data-ui-progress-bar
      style={{
        height: "100%",
        width: `${Math.max(0, Math.min(100, value))}%`,
        backgroundColor: "currentColor",
        transition: "width 240ms ease",
        ...sxToStyle(getNestedStyle(sx, "linear-progress-bar")),
      }}
    />
  </div>
);

export const CircularProgress = ({ size = 24, sx }) => (
  <Loader2 className="animate-spin" style={{ width: size, height: size, ...sxToStyle(sx) }} />
);

export const Alert = ({ children, icon, onClose, severity = "info", sx, style, ...props }) => (
  <div style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
    <BaseAlert tone={toneBySeverity[severity] || "info"}>
      <span className="inline-flex w-full items-start gap-2">
        {icon !== false && icon && <span className="ui-alert-icon inline-flex">{icon}</span>}
        <span className="min-w-0 flex-1">{children}</span>
        {onClose && (
          <button className="border-0 bg-transparent text-slate-500" onClick={onClose} type="button">
            x
          </button>
        )}
      </span>
    </BaseAlert>
  </div>
);

export const Divider = ({ sx, style }) => (
  <hr className="ui-divider" style={{ border: 0, borderTop: "1px solid #e5e7eb", ...sxToStyle(sx), ...(style || {}) }} />
);

export const Menu = ({ children, open, PaperProps = {}, sx }) => {
  if (!open) return null;
  return (
    <div
      className="ui-menu absolute z-50 overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl"
      style={{ minWidth: 200, marginTop: 8, ...sxToStyle(sx), ...sxToStyle(PaperProps.sx), ...(PaperProps.style || {}) }}
    >
      {children}
    </div>
  );
};

export const MenuItem = ({ children, onClick, selected, sx, value, ...props }) => {
  if (!onClick && value !== undefined) {
    return (
      <option value={value} {...props}>
        {children}
      </option>
    );
  }

  return (
    <button
      className={cn("ui-menu-item block w-full border-0 px-3 py-2 text-left text-sm", selected ? "is-selected" : "")}
      data-selected={selected ? "true" : undefined}
      onClick={onClick}
      style={{ backgroundColor: selected ? "rgba(116, 110, 172, 0.1)" : "#ffffff", cursor: "pointer", ...sxToStyle(sx) }}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
};

export const ListItemText = ({ children, primary, secondary }) => (
  <span className="ui-list-item-text">
    {children || (
      <>
        {primary && <span className="block">{primary}</span>}
        {secondary && <span className="block text-xs text-slate-500">{secondary}</span>}
      </>
    )}
  </span>
);

export const ListItemButton = ({ children, className, onClick, selected, sx, style, ...props }) => (
  <button
    className={cn("ui-list-item-button flex w-full items-center border-0 bg-transparent text-left transition", selected ? "is-selected" : "", className)}
    data-selected={selected ? "true" : undefined}
    onClick={onClick}
    style={{ cursor: "pointer", ...sxToStyle(sx), ...(style || {}) }}
    type="button"
    {...props}
  >
    {children}
  </button>
);

export const Radio = ({ checked, onChange, sx, style, value, ...props }) => (
  <input
    checked={checked}
    className="ui-radio"
    onChange={onChange}
    style={{ ...sxToStyle(sx), ...(style || {}) }}
    type="radio"
    value={value}
    {...props}
  />
);

export const Dialog = ({
  BackdropProps,
  children,
  disableEscapeKeyDown,
  fullScreen,
  fullWidth,
  maxWidth = "sm",
  onClose,
  open,
  PaperProps = {},
  scroll,
  TransitionProps,
  ...props
}) => {
  if (!open) return null;
  const width = { xs: 360, sm: 520, md: 720, lg: 960, xl: 1140 }[maxWidth] || 520;
  return (
    <div
      className="ui-dialog-root fixed inset-0 z-50 grid place-items-center px-4 py-6"
      data-disable-escape-keydown={disableEscapeKeyDown ? "true" : undefined}
      data-has-transition-props={TransitionProps ? "true" : undefined}
      {...props}
    >
      <button
        aria-label="Close dialog backdrop"
        className={cn("ui-backdrop absolute inset-0 bg-slate-950/50", BackdropProps?.className)}
        onClick={onClose}
        style={BackdropProps?.style}
        type="button"
      />
      <section
        className={cn("ui-dialog relative max-h-[90vh] overflow-auto rounded-lg bg-white shadow-2xl", PaperProps.className)}
        style={{
          width: fullScreen || fullWidth ? "100%" : undefined,
          height: fullScreen ? "100%" : undefined,
          maxWidth: fullScreen ? "100%" : width,
          overflow: scroll === "paper" ? "auto" : undefined,
          ...sxToStyle(PaperProps.sx),
          ...(PaperProps.style || {}),
        }}
      >
        {children}
      </section>
    </div>
  );
};

export const DialogTitle = ({ children, sx, ...props }) => (
  <div className="ui-dialog-title px-5 py-4" style={sxToStyle(sx)} {...props}>
    {children}
  </div>
);

export const DialogContent = ({ children, sx, ...props }) => (
  <div className="ui-dialog-content px-5 py-4" style={sxToStyle(sx)} {...props}>
    {children}
  </div>
);

export const DialogContentText = ({ children, sx, style, ...props }) => (
  <p className="ui-dialog-content-text text-sm text-slate-600" style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </p>
);

export const DialogActions = ({ children, sx, ...props }) => (
  <div className="ui-dialog-actions flex justify-end gap-2 px-5 py-4" style={sxToStyle(sx)} {...props}>
    {children}
  </div>
);

export const AlertTitle = ({ children, sx }) => (
  <div className="font-semibold" style={sxToStyle(sx)}>
    {children}
  </div>
);

export const SnackbarContent = ({ message, sx }) => (
  <div className="ui-snackbar rounded-md border bg-white p-3 shadow-lg" style={sxToStyle(sx)}>
    {message}
  </div>
);

export const Snackbar = ({ anchorOrigin, autoHideDuration, children, message, onClose, open, sx }) => {
  React.useEffect(() => {
    if (!open || !autoHideDuration || !onClose) return undefined;
    const timer = window.setTimeout(onClose, autoHideDuration);
    return () => window.clearTimeout(timer);
  }, [autoHideDuration, onClose, open]);

  if (!open) return null;
  const verticalClass = anchorOrigin?.vertical === "top" ? "top-5" : "bottom-5";
  return (
    <div className={cn("fixed left-1/2 z-50 -translate-x-1/2", verticalClass)} style={sxToStyle(sx)}>
      {children || <SnackbarContent message={message} />}
    </div>
  );
};

export const Stack = ({ alignItems, children, className, component, direction = "column", flexWrap, justifyContent, spacing = 0, style, sx, useFlexGap, ...props }) => {
  const Component = component || "div";
  return (
    <Component
      className={className}
      data-use-flex-gap={useFlexGap ? "true" : undefined}
      style={{
        alignItems,
        display: "flex",
        flexDirection: direction,
        flexWrap,
        gap: spacing * 8,
        justifyContent,
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      {...props}
    >
    {children}
    </Component>
  );
};

export const Collapse = ({ children, in: isOpen }) => (isOpen ? <>{children}</> : null);

export const LoadingErrorIcon = XCircle;
